import { HeadingRow, ContextMenu, ContextMenuItem, ActivityTracker } from "cai-fusion";
import React, { useContext, useEffect, useRef, useState } from "react";
import DnDFileUpload from "../../fusion/FileUpload/DragAndDrop";
import { useDataStore } from "../contexts/DataStoreContext";
import { debugLog } from "../../../configs/loggingConfig";

import NewDataStoreSubfolderModal from "../overlay/newDataStoreSubfolderModal";
import RenameDataStoreItemModal from "../overlay/renameDataStoreItemModal";
import UploadActivityTracker from "../overlay/uploadActivityTracker";
import { ToastContext } from "cai-fusion";
import { ReactComponent as IconFolder } from "../../../images/icon-folder.svg";
import { ReactComponent as IconFileOutline } from "../../../images/icon-file-outline.svg";
import { ReactComponent as IconTrash } from "../../../images/icon-trash.svg";
import { ReactComponent as IconRename } from "../../../images/icon-rename.svg";
import { ReactComponent as IconDownload } from "../../../images/icon-download.svg";
import { ReactComponent as RippleLoading } from "../../../images/ripple-loading.svg";
import { createUserFeedback } from "../utilities/UploadFeedback";
import { processFiles } from "../../fusion/FileUpload/helpers";
import { rejectToast } from "../utilities/UploadFeedback";
import DataStoreFileMetadataModal from "./overlay/dataStoreFileMetadataModal";
import { useTransientVisibility } from "../../fusion/Contexts/VisualContext";

const DataStoreFileView = () => {
    const { createToast } = useContext(ToastContext);
    const { 
        dataStore, 
        setDataStore, 
        addContentToCurrentDataStore, 
        deleteCurrentDataStoreContent, 
        deleteDataStoreFolder, 
        renameCurrentDataStoreItem, 
        fetchFileMetadata,
        isLoading,

        currFolderId,
        itemList,
        setItemList,
        currPath,
        createSubfolder,
        routeIntoSubfolder,
        routeOutOfSubfolder,
        deleteSubfolder,
        newAddFileToDataStore,
    } = useDataStore();
    
    const uploadRef = useRef();

    // Modal toggle states
    const [hideFolderModal, setHideFolderModal] = useState(true);
    const [hideRenameModal, setHideRenameModal] = useState(true);
    const [hideUploadTracker, setHideUploadTracker] = useState(true);
    const [numCurrentUploads, setNumCurrentUploads] = useState(0);
    // For rename, download, delete, etc.
    const [selectedFile, setSelectedFile] = useState({}); // TODO: this field belongs in the context
    const [fileMetadata, setFileMetadata] = useState("");

    const contentItemsRef = useRef();

    const [contentItems, setContentItems] = useState([]);

    const [uploadingFiles, setUploadingFiles] = useState([]);
    const uploadingFilesRef = useRef();

    const { show } = useTransientVisibility();

    /*
        Navigation - Changing visCurrPath and currTree to enter and exit various folders.
    */

    useEffect(() => {
        if (dataStore?.contentItems) {
            setContentItems([...dataStore?.contentItems]);
        }
    }, [dataStore])

    useEffect(() => {
        contentItemsRef.current = contentItems
    }, [contentItems])

    useEffect(() => {
        uploadingFilesRef.current = uploadingFiles
    }, [uploadingFiles])

    /*
        File Management - Handling of files and folders with API calls, while keeping the local data structure up to date.
    */


    // Refurbished upload file with V3 microservices
    // Realistically this could be moved into the context, but for parity and simplicity we'll keep it here for now.
    const newFile = async (files) => {
        const uploadingFolderId = currFolderId;
        let uploadedItems = [];
        let feedbackList = [];

        // Make sure files are adequately processed and handled
        const processedFiles = await processFiles(files);

        // Add loading state to visual list of files
        let newFileItems = processedFiles.map((file) => ({"name": file.resourceName, "type": "file", "isLoading": true}));
        setItemList([...itemList, ...newFileItems]);

        // Have every upload happen asychronously
        const uploadEvents = processedFiles.map(async (file) => {
            try {
                let newItem = await newAddFileToDataStore(file, uploadingFolderId);
                uploadedItems.push(newItem);
                feedbackList.push({ resourceName: file.resourceName, isSuccess: true });
            } catch (err) {
                feedbackList.push({ resourceName: file.resourceName, isSuccess: false, Message: err.message });
            }
        });

        // Make sure all uploads complete
        await Promise.all(uploadEvents);
        
        // If we haven't moved out of this folder since the upload began, we can remove the loading states.
        if (currFolderId === uploadingFolderId) {
            // At the same time, we can append the uploaded items. Though, we need to clean them up a bit to match the GET structure
            let cleanedUploadItems = uploadedItems.map((item) => ({"name": item.resourceName, "id": item.id, "type": "document"}));
            debugLog.log("[DATASTORES] Cleaned upload items:", cleanedUploadItems);
            setItemList([...itemList.filter((item) => !item.isLoading), ...cleanedUploadItems]);
        }
        // User feedback toast
        createUserFeedback(feedbackList, undefined, createToast);
    }

    const deleteFile = async (fileObj) => {
        // We use a placeholder for type, so that the file will disappear immediately, but the API call will handle deleting it in full.
        // TODO: Some way to handle this so that there aren't race conditions with deletes. Maybe loading state instead of disappear?
        setItemList([...itemList.filter((item) => (item.id !== fileObj.id))]);

        // ACTUALLY do the delete.
        await deleteCurrentDataStoreContent(fileObj.id);
    };

    const renameFile = async (fileObj, newName) => {
        debugLog.log("[DATASTORES] Changing filename");
        // Mark the old file as deleted and create a new one - visually, this will replace the old with the new.
        setItemList([...itemList.map((item) => item.id === fileObj.id ? {...item, "name": newName} : item)])
        // Once this API call is finished, the data store object will be updated to more accurately reflect the change.
        await renameCurrentDataStoreItem(fileObj.id, newName);
    }

    const editMetadata = async (fileObj) => {
        // await api call to get metadata
        setSelectedFile(fileObj);
        try {
            let fileObjMetadata = await fetchFileMetadata(fileObj?.id);
            // Normalise: API may return string, object with fileNotes, or object with value.
            // Ensure the textarea always receives a plain string to avoid [object Object].
            setFileMetadata(
                typeof fileObjMetadata === "string"
                    ? fileObjMetadata
                    : fileObjMetadata?.fileNotes ??
                      fileObjMetadata?.FileNotes ??
                      JSON.stringify(fileObjMetadata)
            );
            show('DataStoreFileMetadataModal');
        } catch (e) {
            debugLog.error("[DATASTORES] Error fetching file metadata.");
        }
    }

    return (
        <div ref={uploadRef}>
            <HeadingRow
                className="m-heading-row--secondary"
                title="Files"
                size={3}
            >
                <div className="m-icon-row m-icon-row--right-align">
                    <button className="a-icon-link m-icon-row__icon" onClick={() => setHideFolderModal(false)}>
                        <div className="a-icon-link__inner">
                            <div className="a-icon-link__icon-wrapper">
                                <IconFolder className="a-icon-link__icon" />
                            </div>
                            <span className="a-icon-link__text">Add Sub-Folder</span>
                        </div>
                    </button>
                </div>
            </HeadingRow>
            <div className="row">
                <div className="col-12">
                    <div className="o-block">
                        { isLoading ?
                        <div className="text-center" aria-colspan={4}>
                            <RippleLoading className="a-icon__img" style={{maxHeight: 50}}/>
                            <p className="a-muted-text">Loading content for this data collection...</p>   
                        </div> :
                        <>
                            { currPath.length > 0 && 
                                <nav className="m-breadcrumbs l-mb-xs">
                                    <ul className="m-breadcrumbs__trail">
                                        <li className="m-breadcrumbs__trail">
                                            <a className="m-breadcrumbs__link" href="#" onClick={
                                                (e) => {e.preventDefault(); routeOutOfSubfolder(null);}}><strong>Files</strong></a>
                                        </li>
                                        {currPath.map((folder, index) => {
                                            if(index === currPath.length - 1){
                                                return (
                                                    <li className="m-breadcrumbs__item m-breadcrumbs__item--active" key={folder.id}>
                                                        <span className="m-breadcrumbs__link">{folder.name}</span>
                                                    </li>
                                                )
                                            }
                                            return (
                                                <li className="m-breadcrumbs__trail" key={folder.id}>
                                                    <a className="m-breadcrumbs__link" href="#" onClick={(e) =>{e.preventDefault(); routeOutOfSubfolder(folder.id)}}><strong>{`/${folder.name}`}</strong></a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </nav>
                            }
                            <div style={{ marginBottom: "20px" }}>
                                <DnDFileUpload 
                                    onFileUpload={newFile}
                                    displayText={`Choose file or drag files here to add to this ${currPath.length === 0 ? "data collection" : "folder"}.`}
                                    targetElementRef={uploadRef}
                                    onRejectedFiles={(files) => {
                                        rejectToast(files, createToast);
                                    }}
                                />
                            </div>
                            {itemList.length === 0 ? 
                            <div className="col-md-12 text-center" aria-colspan={4}>
                                <p className="a-muted-text">{`You have no files in this ${currPath.length === 0 ? "data collection" : "folder"}. Feel free to upload some!`}</p>   
                            </div>
                            :
                            <table className="o-treegrid o-treegrid--compact l-mr-sm">
                                <colgroup className="o-treegrid__colgroup">
                                    <col className="o-treegrid__col o-treegrid__col--48" span={1} />
                                    <col className="o-treegrid__col o-treegrid__col--large" span={1} />
                                    <col className="o-treegrid__col o-treegrid__col--48" span={1} />
                                    <col className="o-treegrid__col o-treegrid__col--48" span={1} />
                                </colgroup>
                                <tbody>
                                    {/* Display the folders first - filter by type, sort by name, then display them */}
                                    {itemList.filter((item) => item.type === "datastore").sort((a, b) => a.name.localeCompare(b.name)).map((folder) => {
                                        return (
                                            <tr className="o-treegrid__row o-treegrid__row--shown" key={folder.id}>
                                                <td className="o-treegrid__cell">
                                                    <span className="a-icon">
                                                        <IconFolder className="a-icon__img" />
                                                    </span>
                                                </td>
                                                <td className="o-treegrid__cell">
                                                    <span className="a-subtle-text">
                                                        <strong>
                                                            <a href="#" onClick={(e) =>{e.preventDefault(); routeIntoSubfolder(folder.id);}}>{ folder.name }</a>
                                                        </strong>
                                                    </span>
                                                </td>
                                                <td className="o-treegrid__cell"/>
                                                <td className="o-treegrid__cell">
                                                    <button className="a-icon-link a-icon-link--destructive" onClick={() => deleteSubfolder(folder.id)}>
                                                        <div className="a-icon-link__inner">
                                                            <div className="a-icon-link__icon-wrapper">
                                                                <IconTrash className="a-icon-link__icon" />
                                                            </div>
                                                        </div>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {itemList.filter((item) => item.type === "file" || item.type === "document").sort((a, b) => a.name.localeCompare(b.name)).map((file) => {
                                        return (
                                            <>
                                                {!file?.isDeleted &&
                                                    <FileItem key={file?.id}
                                                        name={file?.name} 
                                                        obj={file} 
                                                        deleteFile={() => deleteFile(file)}
                                                        renameFile={() => {
                                                            setSelectedFile(file);
                                                            setHideRenameModal(false);
                                                        }}
                                                        editMetadata={() => editMetadata(file)}
                                                    />
                                                }
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                            }
                        </>}
                    </div>
                </div>
            </div>
            <NewDataStoreSubfolderModal 
                isHidden={hideFolderModal}
                onClose={() => setHideFolderModal(true)}
                clickOutsideToClose
                onSubmit={(name) => createSubfolder(name)}
            />
            <RenameDataStoreItemModal 
                isHidden={hideRenameModal}
                onClose={() => setHideRenameModal(true)}
                clickOutsideToClose
                onSubmit={(fileObj, name) => renameFile(fileObj, name)}
                fileItem={selectedFile}
            />
            <DataStoreFileMetadataModal 
                fileItem={selectedFile}
                fileMetadata={fileMetadata}
            />
            <UploadActivityTracker 
                isHidden={hideUploadTracker}
                onClose={() => {setHideUploadTracker(true)}}
                numFiles={numCurrentUploads}
            />
        </div>
    )
}

const FileItem = ({name, obj, deleteFile, renameFile, editMetadata}) => {
    const [menuIsHidden, setMenuIsHidden] = useState(true);
    const { show } = useTransientVisibility(); 

    return (
        <tr className="o-treegrid__row o-treegrid__row--shown">
            <td className="o-treegrid__cell">
                <span className="a-icon">
                    { !obj.isLoading ? 
                        <IconFileOutline className="a-icon__img" /> :
                        <RippleLoading className="a-icon__img" style={{maxHeight: 50}}/>
                    }
                </span>
            </td>
            <td className="o-treegrid__cell">
                <span className="a-subtle-text">
                    <strong>{ name }</strong>
                </span>
            </td>
            <td className="o-treegrid__cell">
                { !obj.isLoading &&
                    <ContextMenu
                        rightAligned
                        isHidden={menuIsHidden}
                        onClick={() => setMenuIsHidden(!menuIsHidden)}
                        onBlur={() => setMenuIsHidden(true)}
                    >
                        <ContextMenuItem 
                            Icon={IconRename}
                            name="Rename File"
                            onClick={renameFile}
                        />
                        <ContextMenuItem 
                            Icon={IconRename}
                            name="Edit File Notes"
                            onClick={editMetadata}
                        />
                        {/* <ContextMenuItem 
                            Icon={IconDownload}
                            name="Download File"
                        /> */}
                    </ContextMenu>
                }
            </td>
            <td className="o-treegrid__cell">
                { !obj.isLoading &&
                    <button className="a-icon-link a-icon-link--destructive" onClick={deleteFile}>
                        <div className="a-icon-link__inner">
                            <div className="a-icon-link__icon-wrapper">
                                <IconTrash className="a-icon-link__icon" />
                            </div>
                        </div>
                    </button>
                }
            </td>
        </tr>
    )
}


export default DataStoreFileView;